import type { FC, JSX } from "react";
import { useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

import { EllipsisDropdownItem, GroupSecondaryLevel } from "@similarweb/ui-components";

import { keywordGroupsQueryClient } from "app/services/keyword-groups";
import { swSettings } from "app/common/services/swSettings";
import DashboardService from "app/components/dashboard/services/DashboardService";
import type {
    IDashboardNavItemConfig,
    DashboardMenuItemType,
    IDashboardItemMenuConfig,
    IDashboardNavItem,
} from "app/components/sidebar3/secondary-side-bar/components/DashboardsNavBar/DashboardNavBarTypes";
import { adaptDashboardItemsData } from "app/components/sidebar3/secondary-side-bar/components/DashboardsNavBar/Services/DashboardNavBarDataHandler";
import {
    defaultGroupItemClickHandler,
    sidebarTrackingDispatcher,
} from "app/components/sidebar3/secondary-side-bar/components/DigitalSuiteNavBarUtils";
import { setDialogIsOpen, setSelectedDashboard } from "app/pages/dashboard/DashboardCopyToUserDialogActions";
import { setSharedWithMeDashboards } from "app/pages/dashboard/DashboardSideNavActions";
import { setDashboardTitle } from "app/pages/dashboard/redux/actions/CurrentDashboardActions";
import { ShareDashboardService } from "app/pages/dashboard/ShareDashboardService";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { SwTrack } from "app/services/SwTrack";

import { DashboardNavItem } from "./NavComponents/DashboardNavItem";

const BodyContainer = styled.div`
    padding-right: 10px;
`;

const DashboardsNavBarBody: FC = () => {
    const dispatch = useDispatch();
    const { customDashboard, routing } = useSelector((state) => state);

    const { currentStateId } = routing;
    const { navList } = customDashboard.dashboardSideNav;
    const { isOpen: copyToUserDialogIsOpen } = customDashboard.dashboardCopyToUserDialog;

    /**
     * Marks if there's an item that is currently in delete mode.
     * an item that is currently deleted shows a delete confirmation dialog above it
     */
    const [currentlyDeletedId, setCurrentlyDeletedId] = useState<string>(null);

    /**
     * Marks if there's an item that is currently in loading mode.
     * an item that is currently loading shows a loader.
     */
    const [currentlyLoadingId, setCurrentlyLoadingId] = useState<string>(null);

    /**
     * Marks if there's an item that is currenty in edit mode.
     * an item that is currently editing opens a rename input for changing its text (title)
     */
    const [currentlyEditedItem, setCurrentlyEditedItem] = useState<IDashboardNavItemConfig>(null);

    /**
     * Runs after deleting an item. Navigates to another nav item.
     */
    const updateNavigationAfterDelete = (): void => {
        dispatch(setSharedWithMeDashboards());

        if (DashboardService.dashboards.length > 0) {
            const firstDashboard = DashboardService.getFirstDashboard();
            proNavigator.go("dashboard-exist", { dashboardId: firstDashboard.id });
            return;
        }

        proNavigator.go("dashboard-gallery");
    };

    /**
     * Runs after editing an item. valides that the new item name is valid.
     */
    const isNewItemTitleValid = (newItemTitle: string): boolean => {
        return DashboardService.validateDashboardTitle(currentlyEditedItem, newItemTitle);
    };

    /**
     * Deletes and un-shares the dashboard nav item from the navbar.
     */
    const handleDeleteDashboard = async (navListItem: any): Promise<void> => {
        const isSharingEnabled = swSettings.components.ShareDashboard.resources.CanShareADashboard;
        if (isSharingEnabled && navListItem.isSharedByMe) {
            await ShareDashboardService.unShare({
                dashboardId: navListItem.id,
            });
        }

        await DashboardService.deleteDashboard({ id: navListItem.id });
        updateNavigationAfterDelete();
    };

    /**
     * Duplicates the dashboard nav item
     */
    const handleDuplicateDashboard = async (navListItem: any): Promise<void> => {
        setCurrentlyLoadingId(navListItem.id);
        const dashboardDuplicateRes = await DashboardService.cloneDashboard(navListItem);
        await keywordGroupsQueryClient.invalidateAll();
        setCurrentlyLoadingId(null);
        dispatch(setSharedWithMeDashboards());

        proNavigator.go("dashboard-created", {
            dashboardId: dashboardDuplicateRes.id,
        });
    };

    /**
     * Shares the dashboard nav item with another user
     */
    const handleShareDashboard = (navListItem: IDashboardNavItem): void => {
        dispatch(setDialogIsOpen(!copyToUserDialogIsOpen));
        dispatch(setSelectedDashboard(navListItem));
    };

    /**
     * Renames the dashboard nav item
     */
    const handleRenameDashboard = async (navListItem: any, newDashboardName: string): Promise<void> => {
        try {
            setCurrentlyLoadingId(navListItem.id);
            await DashboardService.renameDashboard({
                id: navListItem.id,
                title: newDashboardName,
            });
            dispatch(setDashboardTitle(navListItem.id, newDashboardName));
        } finally {
            setCurrentlyLoadingId(null);
            setCurrentlyEditedItem(null);
        }
    };

    const handleItemConfirmDelete = async (navListItem: any): Promise<void> => {
        SwTrack.all.trackEvent("Dashboards", "click", "Side Bar/Delete/Yes");

        setCurrentlyLoadingId(navListItem.id);
        await handleDeleteDashboard(navListItem);

        setCurrentlyDeletedId(null);
        setCurrentlyLoadingId(null);
    };

    const handleItemCancelDelete = (): void => {
        SwTrack.all.trackEvent("Dashboards", "click", "Side Bar/Delete/No");
        setCurrentlyDeletedId(null);
    };

    const handleItemConfirmEdit = async (newItemText: string): Promise<void> => {
        const hasUpdatedText = currentlyEditedItem.title !== newItemText;
        if (!hasUpdatedText) {
            setCurrentlyEditedItem(null);
            return;
        }

        const hasValidNewText = isNewItemTitleValid(newItemText);
        if (!hasValidNewText) {
            setCurrentlyEditedItem(null);
            return;
        }

        SwTrack.all.trackEvent("Dashboards", "click", "Side Bar/Edit/" + newItemText);
        await handleRenameDashboard(currentlyEditedItem, newItemText);
    };

    const handleItemCancelEdit = (): void => {
        setCurrentlyEditedItem(null);
    };

    const handleMenuItemClick = (menuItem: { id: DashboardMenuItemType }, navItem: any) => {
        switch (menuItem.id) {
            case "rename":
                SwTrack.all.trackEvent("Dashboards", "click", "Side Bar/Edit");
                setCurrentlyEditedItem(navItem);
                return;

            case "erase":
                SwTrack.all.trackEvent("Dashboards", "click", "Side Bar/Delete");
                setCurrentlyDeletedId(navItem.id);
                return;

            case "duplicate":
                SwTrack.all.trackEvent("Duplicate Report", "click", "Side Bar");
                handleDuplicateDashboard(navItem);
                return;

            case "share":
                handleShareDashboard(navItem);
                return;

            default:
                return;
        }
    };

    const handleItemClick = (itemId: string, title: string, itemIndex: number, groupTitle: string): void => {
        const trackingDashboardItemProps = {
            depth: 3,
            type: "click",
            report: title,
            report_group: groupTitle,
            report_domain: null,
            analytics_domain: "Dashboards",
            position_order: itemIndex + 1,
            is_dropdown_list: false,
            is_locked: false,
            tag: "none",
        };
        sidebarTrackingDispatcher(trackingDashboardItemProps);
        proNavigator.go("dashboard-exist", {
            dashboardId: itemId,
        });
    };

    const buildMenuItems = (menuItemsConfig: IDashboardItemMenuConfig[]): JSX.Element[] => {
        return menuItemsConfig
            .filter((menuItem) => menuItem.isVisible)
            .map((menuItem) => {
                return (
                    <EllipsisDropdownItem
                        key={menuItem.id}
                        id={menuItem.id}
                        iconName={menuItem.iconName}
                        disabled={menuItem.disabled}
                    >
                        {menuItem.text}
                    </EllipsisDropdownItem>
                );
            });
    };

    const buildNavItems = (): JSX.Element[] => {
        const itemsData = adaptDashboardItemsData(navList, {
            selectedId: currentStateId,
            loadingId: currentlyLoadingId,
            deletedId: currentlyDeletedId,
            editingId: currentlyEditedItem?.id,
        });
        return itemsData.map((groupItem, groupIdx) => {
            const hasActiveChild = groupItem.subItems.some((child) => child.id === currentStateId);

            const itemChildren = groupItem.subItems.map((child: IDashboardNavItemConfig, idx) => {
                return (
                    <DashboardNavItem
                        key={child.id}
                        itemConfig={child}
                        onClick={handleItemClick}
                        onMenuItemClick={handleMenuItemClick}
                        getMenuItems={() => buildMenuItems(child.menuItems)}
                        onConfirmDelete={handleItemConfirmDelete}
                        onCancelDelete={handleItemCancelDelete}
                        onCancelEdit={handleItemCancelEdit}
                        onConfirmEdit={handleItemConfirmEdit}
                        itemIndex={idx}
                        groupTitle={groupItem.title}
                    />
                );
            });
            const trackingGroupProps = {
                depth: 2,
                report: null,
                report_group: groupItem.name,
                report_domain: null,
                analytics_domain: "Dashboards",
                position_order: groupIdx + 1,
                is_dropdown_list: true,
                is_locked: false,
                tag: "none",
            };
            return (
                <GroupSecondaryLevel
                    key={groupItem.name}
                    itemText={groupItem.title}
                    id={groupItem.name}
                    isSelected={hasActiveChild}
                    onGroupItemClick={defaultGroupItemClickHandler(trackingGroupProps)}
                >
                    {itemChildren}
                </GroupSecondaryLevel>
            );
        });
    };

    return <BodyContainer>{buildNavItems()}</BodyContainer>;
};

export default DashboardsNavBarBody;
